import { equipmentWaterSourceId } from '@lib/dto/irrigation.dto'
import type {
  IrrigationEquipmentDto,
  IrrigationSiteDto,
  IrrigationTimerDto,
  WaterSourceDto,
  ZoneDto,
} from '@lib/dto/irrigation.dto'
import {
  IRRIGATION_EQUIPMENT_KIND_LABELS,
  SOURCE_ABSENT_EQUIPMENT,
  type SourceAbsentEquipment,
} from '@lib/enums/irrigation.enum'
import { timerName, zonesInOrder } from '@/irrigation-survey/irrigationLabels'

/*
 * The order a system is recorded in, which is the order its water runs: the
 * source, a well's components, the ball valve, the backflow, the master valve,
 * then the timers, and from each timer its sensor and its zones. The operator
 * asked for the survey to walk a technician through the system in that order
 * before anybody drills into a zone (docs/irrigation.md, "Recording a system
 * in order").
 *
 * A step is recorded once anything of its kind is on the system, "none" when
 * the technician said there is none, and to do otherwise. Live equipment
 * outranks "none": a phone that recorded a backflow offline is right that
 * there is one, whatever another phone said meanwhile.
 */

export type StepStatus = 'recorded' | 'none' | 'todo'

/** The equipment a source's own steps are about. */
export type SourceStepKind = 'WELL_COMPONENT' | SourceAbsentEquipment

export type SystemStep =
  | {
      kind: 'equipment'
      id: string
      equipmentKind: SourceStepKind
      source: WaterSourceDto
      items: IrrigationEquipmentDto[]
      status: StepStatus
    }
  | {
      kind: 'timers'
      id: string
      source: WaterSourceDto
      items: IrrigationTimerDto[]
      status: StepStatus
    }
  | {
      kind: 'sensors'
      id: string
      timer: IrrigationTimerDto
      items: IrrigationEquipmentDto[]
      status: StepStatus
    }
  | {
      kind: 'zones'
      id: string
      timer: IrrigationTimerDto
      items: ZoneDto[]
      status: StepStatus
    }

function statusOf(count: number, saidNone: boolean): StepStatus {
  if (count > 0) return 'recorded'
  return saidNone ? 'none' : 'todo'
}

export function equipmentStepId(
  kind: SourceStepKind,
  sourceId: string,
): string {
  return `equipment:${kind}:${sourceId}`
}

export function sensorStepId(timerId: string): string {
  return `sensors:${timerId}`
}

export function zoneStepId(timerId: string): string {
  return `zones:${timerId}`
}

/** The timers fed by a source, in the order they were recorded. */
export function timersOn(
  site: IrrigationSiteDto,
  sourceId: string,
): IrrigationTimerDto[] {
  return site.timers.filter((timer) => timer.waterSourceId === sourceId)
}

/** One source's system, step by step, in order. */
export function systemSteps(
  site: IrrigationSiteDto,
  source: WaterSourceDto,
): SystemStep[] {
  const onSource = site.equipment.filter(
    (item) => equipmentWaterSourceId(item) === source.id,
  )
  const steps: SystemStep[] = []
  // A well always has a pump, so there is no "none" for its components.
  if (source.kind === 'WELL') {
    const items = onSource.filter((item) => item.kind === 'WELL_COMPONENT')
    steps.push({
      kind: 'equipment',
      id: equipmentStepId('WELL_COMPONENT', source.id),
      equipmentKind: 'WELL_COMPONENT',
      source,
      items,
      status: statusOf(items.length, false),
    })
  }
  for (const equipmentKind of SOURCE_ABSENT_EQUIPMENT) {
    const items = onSource.filter((item) => item.kind === equipmentKind)
    steps.push({
      kind: 'equipment',
      id: equipmentStepId(equipmentKind, source.id),
      equipmentKind,
      source,
      items,
      status: statusOf(
        items.length,
        source.absentEquipment.includes(equipmentKind),
      ),
    })
  }
  const timers = timersOn(site, source.id)
  steps.push({
    kind: 'timers',
    id: `timers:${source.id}`,
    source,
    items: timers,
    status: statusOf(timers.length, false),
  })
  const zones = zonesInOrder(site)
  for (const timer of timers) {
    const sensors = site.equipment.filter(
      (item) => item.kind === 'SENSOR' && item.timerId === timer.id,
    )
    steps.push({
      kind: 'sensors',
      id: sensorStepId(timer.id),
      timer,
      items: sensors,
      status: statusOf(
        sensors.length,
        timer.absentEquipment.includes('SENSOR'),
      ),
    })
    const timerZones = zones.filter((zone) => zone.timerId === timer.id)
    steps.push({
      kind: 'zones',
      id: zoneStepId(timer.id),
      timer,
      items: timerZones,
      status: statusOf(timerZones.length, false),
    })
  }
  return steps
}

/** The first step still to do, at or after `from`; null when none is. */
export function nextTodo(
  steps: readonly SystemStep[],
  from = 0,
): SystemStep | null {
  return (
    steps.slice(Math.max(from, 0)).find((step) => step.status === 'todo') ??
    null
  )
}

/** Where a sheet stands in its source's system. */
export type StepAnchor =
  | { kind: 'source'; sourceId: string }
  /** On a step's record: the next step is the one after it. */
  | { kind: 'after'; sourceId: string; stepId: string }
  /** On a timer: its own sensor and zones come next. */
  | { kind: 'timer'; sourceId: string; timerId: string }

/**
 * The next step from where a sheet stands, so finishing one record can lead
 * straight on to the next. A step skipped stays to do on the source's list,
 * but going on from a later one does not send anybody back to it.
 */
export function nextStepFrom(
  site: IrrigationSiteDto,
  anchor: StepAnchor,
): SystemStep | null {
  const source = site.waterSources.find((item) => item.id === anchor.sourceId)
  if (source === undefined) return null
  const steps = systemSteps(site, source)
  switch (anchor.kind) {
    case 'source':
      return nextTodo(steps)
    case 'after': {
      const index = steps.findIndex((step) => step.id === anchor.stepId)
      return index === -1 ? null : nextTodo(steps, index + 1)
    }
    case 'timer': {
      const index = steps.findIndex(
        (step) => step.id === sensorStepId(anchor.timerId),
      )
      return index === -1 ? null : nextTodo(steps, index)
    }
  }
}

/** What a step is called, by itself or beside another timer's. */
export function stepLabel(
  step: SystemStep,
  site: IrrigationSiteDto,
  options: { withTimer?: boolean } = {},
): string {
  const ofTimer = (timer: IrrigationTimerDto, label: string) =>
    options.withTimer === true
      ? `${label} on ${timerName(timer, site.timers)}`
      : label
  switch (step.kind) {
    case 'equipment':
      return step.equipmentKind === 'WELL_COMPONENT'
        ? 'Well components'
        : IRRIGATION_EQUIPMENT_KIND_LABELS[step.equipmentKind]
    case 'timers':
      return step.items.length > 1 ? 'Timers' : 'Timer'
    case 'sensors':
      return ofTimer(step.timer, 'Sensor')
    case 'zones':
      return ofTimer(step.timer, 'Zones')
  }
}

/**
 * Equipment of a step's kind that is on no live source or timer: recorded
 * before the steps existed, when the Add menu dropped a backflow or a sensor
 * with nothing to say what it was on. Shown on the step, to be linked, so it
 * is neither recorded a second time nor answered "none here".
 */
export function unlinkedFor(
  step: SystemStep,
  site: IrrigationSiteDto,
): IrrigationEquipmentDto[] {
  if (step.kind === 'equipment' && step.equipmentKind !== 'WELL_COMPONENT') {
    const sourceIds = new Set(site.waterSources.map((source) => source.id))
    return site.equipment.filter((item) => {
      if (item.kind !== step.equipmentKind) return false
      const waterSourceId = equipmentWaterSourceId(item)
      return waterSourceId === null || !sourceIds.has(waterSourceId)
    })
  }
  if (step.kind === 'sensors') {
    const timerIds = new Set(site.timers.map((timer) => timer.id))
    return site.equipment.filter(
      (item) =>
        item.kind === 'SENSOR' &&
        (item.timerId === null || !timerIds.has(item.timerId)),
    )
  }
  return []
}

/**
 * Ball valves, backflows and master valves on no water source, and sensors
 * on no timer: recorded before the steps existed, and waiting to be told.
 */
export function equipmentWithoutParent(
  site: IrrigationSiteDto,
): IrrigationEquipmentDto[] {
  const sourceIds = new Set(site.waterSources.map((source) => source.id))
  const timerIds = new Set(site.timers.map((timer) => timer.id))
  return site.equipment.filter((item) => {
    switch (item.kind) {
      case 'BALL_VALVE':
      case 'BACKFLOW':
      case 'MASTER_VALVE':
        return item.waterSourceId === null || !sourceIds.has(item.waterSourceId)
      case 'SENSOR':
        return item.timerId === null || !timerIds.has(item.timerId)
      case 'VALVE':
      case 'VALVE_BOX':
      case 'WELL_COMPONENT':
      case 'OTHER':
        return false
    }
  })
}

/** Timers recorded before timers had a source, still waiting to be given one. */
export function timersWithoutSource(
  site: IrrigationSiteDto,
): IrrigationTimerDto[] {
  const sourceIds = new Set(site.waterSources.map((source) => source.id))
  return site.timers.filter(
    (timer) =>
      timer.waterSourceId === null || !sourceIds.has(timer.waterSourceId),
  )
}
